import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, CSP_NONCE, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateModule } from '@ngx-translate/core';
import { environment, keycloakAngularConfig } from '@env/environment';
import { RouteReusableStrategy, ApiPrefixInterceptor, ErrorHandlerInterceptor } from '@shared';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LoginModule } from './auth/login/login.module';
import { CoreModule } from './core/core.module';
import { MyStartupService } from './app.service';
import { BootstrapComponent } from './bootstrap.component';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import { NgxPermissionsModule } from 'ngx-permissions';
import { KeycloakService } from 'keycloak-angular';
import { InlineStylesCSPModule } from '../csp/inline-styles-csp.module';
import { Logger } from './core';

const log = new Logger('initializeKeycloak');

export function startupServiceFactory(startupService: MyStartupService): Function {
  return () => startupService.load();
}

function initializeKeycloak(keycloak: KeycloakService) {
  return () => {
    if (JSON.parse(localStorage.getItem('isSSOLogin') || 'null')) return;

    var initOptions: Keycloak.KeycloakInitOptions = {
      redirectUri: keycloakAngularConfig.redirectUri,
      onLoad: 'check-sso',
      //onLoad: 'login-required',
      pkceMethod: 'S256',
      silentCheckSsoRedirectUri: keycloakAngularConfig.silentCheckSsoRedirectUri,
      enableLogging: keycloakAngularConfig.enableLogging,
      checkLoginIframe: keycloakAngularConfig.checkLoginIframe,
    };

    let allowedPaths = [
      'resetpassword',
      'forgotpassword',
      'sso',
      'oops',
      'idle-timeout',
      'session-message',
      'signout-message',
      'code-verification',
      'recovery-code',
      'recovery-code-verification',
      'set-password',
      'post-logout',
      'ext-signout-callback',
      'forbidden',
    ];

    //need login only for auth code flow or browser redirect...
    let isPathAllowed = allowedPaths.find((a) => location.pathname.includes(a));
    if (isPathAllowed) {
      initOptions.redirectUri = window.location.href;
    }

    let keycloakConfig = keycloakAngularConfig.clients.default;
    if (window.location.origin.indexOf(keycloakAngularConfig.dgsUrlKey) != -1)
      //if (window.location.origin.indexOf('portmore') != -1)
      keycloakConfig = keycloakAngularConfig.clients.dgs;
    return keycloak
      .init({
        config: {
          url: keycloakConfig.authority,
          realm: keycloakConfig.realm,
          clientId: keycloakAngularConfig.clientId,
        },
        initOptions,
        loadUserProfileAtStartUp: true,
        //bearerExcludedUrls: ['api'],
      })
      .then((auth) => {})
      .catch((error: any) => {
        console.log(error);
        console.trace();
        log.error('Keycloak Error', JSON.stringify(error));
        //log.error('[app.module.ts] line 84, window.localStorage clear() called');
        window.localStorage.clear();
        console.log(new Date().toLocaleString() + ' initializeKeycloak ➤  window.localStorage.clear()');
      });
  };
}

@NgModule({
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.register('./ngsw-worker.js', { enabled: environment.production }),
    FormsModule,
    HttpClientModule,
    RouterModule,
    TranslateModule.forRoot(),
    NgxPermissionsModule.forRoot(),
    CoreModule,
    LoginModule,
    SharedModule.forRoot(),
    InlineStylesCSPModule,
    AppRoutingModule, // must be imported as the last module as it contains the fallback route
  ],
  declarations: [AppComponent, BootstrapComponent],
  providers: [
    MyStartupService,
    KeycloakService,
    {
      provide: CSP_NONCE,
      useValue: 'portal-css',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPrefixInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy,
    },
    { provide: APP_INITIALIZER, useFactory: initializeKeycloak, multi: true, deps: [KeycloakService] },
    { provide: APP_INITIALIZER, useFactory: startupServiceFactory, deps: [MyStartupService], multi: true },
  ],
  bootstrap: [BootstrapComponent],
})
export class AppModule {}
